import { Field } from "@/types/Field";
import { guestCatalogueOf, type CatalogueEntry } from "@/lib/authz/fieldExclusion";
import { apiPost, apiGet, apiDelete, apiPut } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/additional-fields/";

export async function createAdditionalField(
  eventId: string,
  field: Field,
  /**
   * Art. 50 Abs. 2 KI-VO (J6): the AI lane behind this create (the JSON
   * transfer of a marked / pre-wave file sends `template_import`). The body
   * IS the entity ⇒ `?aiContentTool=`; the server stamps the event mark.
   */
  aiContentTool?: string,
): Promise<string> {
  return await apiPost<string>(withAiContentTool(`${BASE_PATH}${eventId}`, aiContentTool), field, {
    "Cache-Control": "no-store",
  });
}

export async function getAdditionalFields(
  eventId: string,
  fields: string,
  /** Ride the admin's own auth instead of an active preview bearer — the role
   *  editor needs the FULL field catalogue, not the previewed role's view. */
  bypassPreview = false,
  /** Entity scope of the definitions (Wave A). Omitted = GUEST (backward
   *  compatible — the 20+ existing guest call sites stay byte-identical). */
  target?: "GUEST" | "SPEAKER" | "SPONSOR",
): Promise<Field[]> {
  const targetParam = target ? `&target=${target}` : "";
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent(fields)}${targetParam}`;
  const headers: Record<string, string> = { "Cache-Control": "no-store" };
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";

  try {
    return await apiGet<Field[]>(url, headers, true);
  } catch {
    return [];
  }
}

/**
 * The event's GUEST field definitions for a GATE — `name`, `sensitivity`,
 * `appliesTo` — and, unlike {@link getAdditionalFields}, it THROWS on every
 * failure (403 for a role without guests:read, a 5xx, a network error, a
 * body that is not a list). A gate that read "no definitions" from a failed
 * request would read "nothing is protected" (ship 2026-10-06 audit r2
 * HIGH-1); the caller decides what an unreadable answer means.
 */
export async function getGuestFieldSensitivities(
  eventId: string,
): Promise<Array<Pick<Field, "name" | "sensitivity" | "appliesTo">>> {
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent("name,sensitivity,appliesTo")}`;
  const body = await apiGet<unknown>(url, { "Cache-Control": "no-store" }, true);
  if (!Array.isArray(body)) throw new Error("fieldDefinitionsUnreadable");
  return body as Array<Pick<Field, "name" | "sensitivity" | "appliesTo">>;
}

/**
 * B2: the role editor's guest field CATALOGUE — every built-in allowlistable
 * column plus every GUEST custom definition (`appliesTo` absent = GUEST),
 * with sensitivity (absent = NORMAL, a level this bundle does not know =
 * null = treated as sensitive) and label. LOUD like
 * {@link getGuestFieldSensitivities}: a 403, a 5xx, a network error or a body
 * that is not a list THROWS. The editor reads a failure as "catalogue
 * unknown" (read-only pane, no orphan marking, no badge) — never as "the
 * event has no custom fields", which would mark every granted custom key as
 * deleted. `bypassPreview` rides the admin's own auth under an active preview.
 *
 * `fresh` (contract §8, B2 F1): `&fresh=true` makes the backend read the
 * definitions from Mongo instead of its shared field cache (which a raise can
 * leave answering the old level for minutes). For the checks that must not
 * trust a cached list — the pre-save sensitive re-check, "Load latest", the
 * re-seed; the pane's own read stays on the cached path. An older backend
 * ignores the parameter. `signal` lets such a check abandon the read (B2 F2:
 * it gives up after a timeout).
 */
export async function getGuestFieldCatalogue(
  eventId: string,
  {
    bypassPreview = false,
    fresh = false,
    signal,
  }: { bypassPreview?: boolean; fresh?: boolean; signal?: AbortSignal } = {},
): Promise<CatalogueEntry[]> {
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent("name,translation,sensitivity,appliesTo")}${fresh ? "&fresh=true" : ""}`;
  const headers: Record<string, string> = { "Cache-Control": "no-store" };
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  const body = signal
    ? await apiGet<unknown>(url, headers, true, undefined, { signal })
    : await apiGet<unknown>(url, headers, true);
  if (!Array.isArray(body)) throw new Error("fieldDefinitionsUnreadable");
  return guestCatalogueOf(body);
}

export async function updateAdditionalField(
  eventId: string,
  fieldId: string,
  field: Partial<Field>,
  /** Art. 50 KI-VO (J6): see `createAdditionalField` — the transfer's option merge. */
  aiContentTool?: string,
): Promise<void> {
  await apiPut<string>(withAiContentTool(`${BASE_PATH}${eventId}/${fieldId}`, aiContentTool), field, {
    "Cache-Control": "no-store",
  });
}

export async function deleteAdditionalField(
  eventId: string,
  fieldId: string
): Promise<void> {
  await apiDelete<string>(`${BASE_PATH}${eventId}/${fieldId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Per-option guest usage counts for a SELECT field (option -> #guests holding
 * that value). Feeds the "N Gäste betroffen" warning before an option is
 * removed. Throws on failure — callers decide the fallback (warn generically).
 */
export async function getAdditionalFieldOptionUsage(
  eventId: string,
  fieldId: string
): Promise<Record<string, number>> {
  return await apiGet<Record<string, number>>(
    `${BASE_PATH}${eventId}/${fieldId}/option-usage`,
    { "Cache-Control": "no-store" },
  );
}
